import { useService } from '@toeverything/infra';
import { type PropsWithChildren, useLayoutEffect } from 'react';

import { WorkbenchService } from '../../workbench/services/workbench';
import {
  FEATURE_KEY_COLLECTIONS,
  FEATURE_KEY_TAGS,
} from './product-feature-gates';
import { useFeatureGate } from './use-feature-gate';

export function useCollectionsFeatureEnabled() {
  const { isFeatureAllowed } = useFeatureGate();
  return isFeatureAllowed(FEATURE_KEY_COLLECTIONS);
}

export function useTagsFeatureEnabled() {
  const { isFeatureAllowed } = useFeatureGate();
  return isFeatureAllowed(FEATURE_KEY_TAGS);
}

export type PlanFeatureAccess =
  | { kind: 'pending' }
  | { kind: 'allowed' }
  | { kind: 'hidden' }
  | { kind: 'locked'; requiredPlan: string };

/**
 * Plan access to a view that needs several features. A missing feature the
 * plan catalog hides, or that no plan offers, is not for sale: the view must
 * not show an upgrade prompt for it.
 */
export function usePlanFeatureAccess(
  featureKeys: readonly string[]
): PlanFeatureAccess {
  const { features, isLoading, isFeatureAllowed, getRequiredPlan } =
    useFeatureGate();
  if (isLoading && features.length === 0) return { kind: 'pending' };
  const missing = featureKeys.filter(key => !isFeatureAllowed(key));
  if (missing.length === 0) return { kind: 'allowed' };
  const offeredPlans = missing.map(key =>
    features.find(feature => feature.featureKey === key)?.visibility ===
    'visible'
      ? getRequiredPlan(key)
      : null
  );
  const [requiredPlan] = offeredPlans;
  if (!requiredPlan || offeredPlans.includes(null)) return { kind: 'hidden' };
  return { kind: 'locked', requiredPlan };
}

/**
 * A gated view opened by URL goes to the workspace overview. It runs inside
 * the view's own router, so it changes the view location through the
 * workbench; a router redirect would leave the view on its loading screen.
 */
export function WorkspaceAllRedirect() {
  const workbench = useService(WorkbenchService).workbench;

  useLayoutEffect(() => {
    workbench.openAll({ replaceHistory: true });
  }, [workbench]);

  return null;
}

export function CollectionsFeatureGuard({ children }: PropsWithChildren) {
  return useCollectionsFeatureEnabled() ? (
    <>{children}</>
  ) : (
    <WorkspaceAllRedirect />
  );
}

export function TagsFeatureGuard({ children }: PropsWithChildren) {
  return useTagsFeatureEnabled() ? <>{children}</> : <WorkspaceAllRedirect />;
}
